import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Combobox } from '@/components/ui/combobox';
import { trpc } from '@/lib/trpc';
import { showSuccessToast, showErrorToast } from '@/hooks/use-mutation-with-toast';
import { usePermission } from '@/components/guards/permission-guard';
import { MoveCariTypeDialog } from './cari/move-cari-type-dialog';
import { cariTypeLabel } from './cari/cari-type-display';

const paymentTermsOptions = [
  { value: 'PREPAID', label: 'Prepaid' },
  { value: 'CREDIT_15', label: 'Net 15 Days' },
  { value: 'CREDIT_30', label: 'Net 30 Days' },
  { value: 'CREDIT_60', label: 'Net 60 Days' },
] as const;

type PaymentTerms = typeof paymentTermsOptions[number]['value'];

interface SupplierFormData {
  companyName: string;
  contactName?: string;
  email?: string;
  phone?: string;
  website?: string;
  address?: string;
  city?: string;
  country?: string;
  taxId?: string;
  paymentTerms?: PaymentTerms;
  notes?: string;
  isActive: boolean;
}

interface Supplier {
  id: string;
  cariTypeId?: string | null;
  cariTypeCode?: string;
  cariType?: { id: string; code: string; name: string; nameEn?: string | null } | null;
  companyName: string;
  contactName?: string | null;
  email?: string | null;
  phone?: string | null;
  website?: string | null;
  address?: string | null;
  city?: string | null;
  country?: string | null;
  taxId?: string | null;
  paymentTerms?: PaymentTerms | null;
  notes?: string | null;
  isActive: boolean;
}

interface SupplierModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  supplier?: Supplier;
}

export function SupplierModal({ open, onOpenChange, supplier }: SupplierModalProps) {
  const { t, i18n } = useTranslation('trading');
  const { t: tCommon } = useTranslation('common');
  const navigate = useNavigate();
  const canMoveType = usePermission('ACCOUNTING', 'EDIT', 'TRADING');
  const [moveTypeOpen, setMoveTypeOpen] = useState(false);
  const { data: cariTypes } = trpc.tradingCariType.list.useQuery(undefined, { enabled: open && canMoveType });
  const currentType = supplier?.cariType
    ?? cariTypes?.find((tp) => tp.id === supplier?.cariTypeId)
    ?? cariTypes?.find((tp) => tp.code === (supplier?.cariTypeCode ?? 'T'));
  const currentTypeId = supplier?.cariTypeId ?? currentType?.id ?? '';
  const currentTypeLabel = currentType
    ? `${currentType.code} · ${cariTypeLabel(currentType, i18n.language)}`
    : `${supplier?.cariTypeCode ?? 'T'} · ${t('cari.supplier')}`;
  const isEditing = !!supplier;
  useEffect(() => setMoveTypeOpen(false), [open, supplier?.id]);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
    setValue,
  } = useForm<SupplierFormData>({
    defaultValues: {
      companyName: '',
      contactName: '',
      email: '',
      phone: '',
      website: '',
      address: '',
      city: '',
      country: '',
      taxId: '',
      paymentTerms: 'CREDIT_30',
      notes: '',
      isActive: true,
    },
  });

  const utils = trpc.useUtils();

  const createMutation = trpc.tradingSupplier.create.useMutation({
    onSuccess: () => {
      utils.tradingSupplier.list.invalidate();
      showSuccessToast(t('common.created'));
      onOpenChange(false);
      reset();
    },
    onError: (error) => {
      showErrorToast(error.message);
    },
  });

  const updateMutation = trpc.tradingSupplier.update.useMutation({
    onSuccess: () => {
      utils.tradingSupplier.invalidate();
      utils.tradingCari.invalidate();
      showSuccessToast(t('common.updated'));
      onOpenChange(false);
    },
    onError: (error) => {
      showErrorToast(error.message);
    },
  });

  useEffect(() => {
    if (open) {
      if (supplier) {
        reset({
          companyName: supplier.companyName,
          contactName: supplier.contactName || '',
          email: supplier.email || '',
          phone: supplier.phone || '',
          website: supplier.website || '',
          address: supplier.address || '',
          city: supplier.city || '',
          country: supplier.country || '',
          taxId: supplier.taxId || '',
          paymentTerms: supplier.paymentTerms || 'CREDIT_30',
          notes: supplier.notes || '',
          isActive: supplier.isActive,
        });
      } else {
        reset({
          companyName: '',
          contactName: '',
          email: '',
          phone: '',
          website: '',
          address: '',
          city: '',
          country: '',
          taxId: '',
          paymentTerms: 'CREDIT_30',
          notes: '',
          isActive: true,
        });
      }
    }
  }, [open, supplier, reset]);

  const onSubmit = (data: SupplierFormData) => {
    const payload = {
      ...data,
      contactName: data.contactName || undefined,
      email: data.email || undefined,
      phone: data.phone || undefined,
      website: data.website || undefined,
      address: data.address || undefined,
      city: data.city || undefined,
      country: data.country || undefined,
      taxId: data.taxId || undefined,
      paymentTerms: data.paymentTerms || undefined,
      notes: data.notes || undefined,
    };

    if (isEditing && supplier) {
      updateMutation.mutate({ id: supplier.id, ...payload });
    } else {
      createMutation.mutate(payload);
    }
  };

  const isSubmitting = createMutation.isPending || updateMutation.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {isEditing ? t('purchasing.suppliers.editSupplier') : t('purchasing.suppliers.addSupplier')}
          </DialogTitle>
          <DialogDescription>
            {isEditing
              ? t('purchasing.suppliers.editSupplier')
              : t('purchasing.suppliers.subtitle')}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="supplierCariType">{t('cari.type')}</Label>
            <div className="flex items-center gap-2">
              <Input id="supplierCariType" value={currentTypeLabel} readOnly />
              {supplier && canMoveType && (
                <Button type="button" variant="outline" disabled={!currentTypeId}
                  onClick={() => setMoveTypeOpen(true)}>
                  {t('cariTypes.moveCari')}
                </Button>
              )}
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="companyName">{t('purchasing.suppliers.companyName')}</Label>
            <Input
              id="companyName"
              {...register('companyName', { required: t('common.required') })}
              placeholder="Acme Corp"
            />
            {errors.companyName && (
              <p className="text-sm text-red-500">{errors.companyName.message}</p>
            )}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="contactName">{t('purchasing.suppliers.contactName')}</Label>
              <Input
                id="contactName"
                {...register('contactName')}
                placeholder="John Doe"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="taxId">{t('purchasing.suppliers.taxId')}</Label>
              <Input
                id="taxId"
                {...register('taxId')}
                placeholder="123456789"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="email">{t('purchasing.suppliers.email')}</Label>
              <Input
                id="email"
                type="email"
                {...register('email')}
                placeholder="supplier@example.com"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="phone">{t('purchasing.suppliers.phone')}</Label>
              <Input
                id="phone"
                {...register('phone')}
                placeholder="+1 234 567 890"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="website">{t('purchasing.suppliers.website')}</Label>
            <Input
              id="website"
              type="url"
              {...register('website')}
              placeholder="https://supplier.com"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="address">{t('purchasing.suppliers.address')}</Label>
            <Input
              id="address"
              {...register('address')}
              placeholder="123 Industrial Road"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="city">{t('purchasing.suppliers.city')}</Label>
              <Input
                id="city"
                {...register('city')}
                placeholder="Istanbul"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="country">{t('purchasing.suppliers.country')}</Label>
              <Input
                id="country"
                {...register('country')}
                placeholder="Turkey"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label>{t('purchasing.suppliers.paymentTerms')}</Label>
            <Combobox
              options={paymentTermsOptions.map(t => ({ value: t.value, label: t.label }))}
              value={watch('paymentTerms') || 'CREDIT_30'}
              onValueChange={(value) => setValue('paymentTerms', value as PaymentTerms)}
              placeholder={t('purchasing.suppliers.selectPaymentTerms')}
              searchPlaceholder={t('common.search')}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="notes">{t('purchasing.suppliers.notes')}</Label>
            <Textarea
              id="notes"
              {...register('notes')}
              rows={3}
            />
          </div>

          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="isActive"
              {...register('isActive')}
              className="h-4 w-4 rounded border-gray-300"
            />
            <Label htmlFor="isActive">{t('purchasing.suppliers.isActive')}</Label>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isSubmitting}
            >
              {tCommon('cancel')}
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {isEditing ? tCommon('save') : tCommon('create')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
      <MoveCariTypeDialog
        cari={open && moveTypeOpen && supplier ? { id: supplier.id, companyName: supplier.companyName } : null}
        currentTypeId={currentTypeId}
        onOpenChange={setMoveTypeOpen}
        onMoved={(code) => {
          void utils.tradingSupplier.invalidate();
          void utils.tradingCari.invalidate();
          void utils.tradingCariType.invalidate();
          setMoveTypeOpen(false);
          onOpenChange(false);
          navigate(`/trading/cari?type=${encodeURIComponent(code ?? supplier?.cariTypeCode ?? 'T')}`);
        }}
      />
    </Dialog>
  );
}
